import { useState, useEffect } from "react";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { useNavigate } from "react-router-dom";
import { notifyError, notifySuccess } from "../utils/notify";
import { kritischeAenderungBestaetigen } from "../utils/veranstaltungstag";

export default function EditTeamModal({ open, onClose, onSaved, onDeleted, team }) {
  const [teamName, setTeamName] = useState("");
  const [eventType, setEventType] = useState("Kinder");
  const [teamType, setTeamType] = useState("Männer");
  const [members, setMembers] = useState(["", "", "", "", ""]);
  const [ages, setAges] = useState([0, 0, 0, 0, 0]);
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [loeschend, setLoeschend] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();

  // === Vorbefüllung beim Öffnen ===
  useEffect(() => {
    if (team && open) {
      setTeamName(team.team_name || "");
      setEventType(team.event_type || "Kinder");
      setTeamType(team.team_type || "Männer");
      setContactEmail(team.contact_email || "");
      setContactPhone(team.contact_phone || "");

      setMembers([
        team.member1 || "",
        team.member2 || "",
        team.member3 || "",
        team.member4 || "",
        team.member5 || "",
      ]);

      setAges([
        team.member1_age || 0,
        team.member2_age || 0,
        team.member3_age || 0,
        team.member4_age || 0,
        team.member5_age || 0,
      ]);
    }
  }, [team, open]);

  // === Speichern ===
  const handleSave = async () => {
    if (!teamName) return alert("Teamname darf nicht leer sein.");

    // Kritisch sind nur Felder, die in die Wertung eingehen: Kategorie und
    // Teamart (Rangliste) sowie bei Kindern die Alter (Altersbonus).
    // Namen und Kontaktdaten zu korrigieren, ist auch am Veranstaltungstag harmlos.
    const bisherigeAlter = [1, 2, 3, 4, 5].map((i) => team[`member${i}_age`] || 0);
    const wertungGeaendert =
      eventType !== (team.event_type || "Kinder") ||
      teamType !== (team.team_type || "Männer") ||
      (eventType === "Kinder" && ages.some((a, i) => Number(a || 0) !== Number(bisherigeAlter[i])));
    if (
      wertungGeaendert &&
      !(await kritischeAenderungBestaetigen(
        `Kategorie, Teamart oder Alter von "${team.team_name}" ändern – ` +
          "Platzierung und Altersbonus des Teams können sich verschieben."
      ))
    ) return;

    const payload = {
      team_name: teamName,
      event_type: eventType,
      team_type: teamType,
      member1: members[0],
      member2: members[1],
      member3: members[2],
      member4: members[3],
      member5: members[4] || null,
      contact_email: contactEmail,
      contact_phone: contactPhone.trim() || null,
    };

    if (eventType === "Kinder") {
      payload.member1_age = ages[0];
      payload.member2_age = ages[1];
      payload.member3_age = ages[2];
      payload.member4_age = ages[3];
      payload.member5_age = ages[4] || null;
    }

    try {
      // Adressiert wird ueber die stabile Team-ID. `team_name` ist seit der
      // ID-Umstellung ein gewoehnliches, aenderbares Feld. Der Rueckfall auf den
      // Namen trifft nur Team-Dokumente von vor der Umstellung – dort ist der
      // Name zugleich die Dokument-ID.
      const data = await fetchWithAuth(
        `${BACKEND_URL}/teams/${encodeURIComponent(team.id || team.team_name)}`,
        navigate,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!data || data.error) {
        console.warn("Fehler beim Aktualisieren des Teams:", data);
        return notifyError(data?.error || "Fehler beim Speichern");
      }

      onSaved();
      onClose();
      notifySuccess("Team erfolgreich aktualisiert!");
    } catch (err) {
      console.error("Fehler beim Aktualisieren des Teams:", err);
      notifyError("Fehler beim Speichern: " + (err.message || err));
    }
  };

  // === Loeschen ===
  //
  // Zweistufige Rueckfrage nach dem Muster des System-Resets in AdminExport:
  // der Vorgang loescht auch alle bereits eingetragenen Stationsergebnisse des
  // Teams und ist nicht rueckgaengig zu machen.
  const handleDelete = async () => {
    if (!(await kritischeAenderungBestaetigen(`Team "${team.team_name}" löschen.`))) return;

    const bestaetigt = window.confirm(
      `⚠️ Team "${team.team_name}" wirklich löschen?\n\n` +
        "Alle bereits eingetragenen Ergebnisse dieses Teams gehen dabei verloren.\n" +
        "Der Vorgang kann nicht rückgängig gemacht werden."
    );
    if (!bestaetigt) return;

    const eingabe = window.prompt(
      `Bitte gib zur Bestätigung den Teamnamen ein: ${team.team_name}`
    );
    if (eingabe !== team.team_name) {
      if (eingabe !== null) notifyError("Eingabe stimmt nicht – Löschen abgebrochen.");
      return;
    }

    setLoeschend(true);
    try {
      const data = await fetchWithAuth(
        `${BACKEND_URL}/teams/${encodeURIComponent(team.id || team.team_name)}`,
        navigate,
        { method: "DELETE" }
      );

      if (!data || data.error) {
        return notifyError(data?.error || "Fehler beim Löschen");
      }

      // Reihenfolge: erst schliessen, dann neu laden – sonst rendert die Liste
      // kurz noch mit dem geloeschten Team im offenen Dialog.
      onClose();
      if (onDeleted) onDeleted();
      notifySuccess(`Team "${team.team_name}" wurde gelöscht.`);
    } catch (err) {
      console.error("Fehler beim Löschen des Teams:", err);
      notifyError("Fehler beim Löschen: " + (err.message || err));
    } finally {
      setLoeschend(false);
    }
  };

  if (!open) return null;

  // === UI ===
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-secondary-950 bg-opacity-75 z-50">
      <div className="bg-secondary-900 p-6 rounded shadow-lg w-full max-w-md text-white border border-secondary-800">
        <h2 className="text-xl font-bold mb-4 text-white">
          Team bearbeiten
        </h2>

        {/* Umbenennen ist seit der Umstellung auf stabile Team-IDs gefahrlos
            moeglich: die Ergebnisse haengen an `team_id`, nicht am Namen.
            Vorher war das Feld deshalb `disabled`. */}
        <input
          type="text"
          placeholder="Teamname"
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          className="w-full bg-secondary-800 text-white border border-secondary-700 p-2 mb-2 rounded placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />

        <select
          value={eventType}
          onChange={(e) => setEventType(e.target.value)}
          className="w-full bg-secondary-800 text-white border border-secondary-700 p-2 mb-4 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="Kinder">Kinder</option>
          <option value="Erwachsene">Erwachsene</option>
        </select>

        <select
          value={teamType}
          onChange={(e) => setTeamType(e.target.value)}
          className="w-full bg-secondary-800 text-white border border-secondary-700 p-2 mb-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">Bitte wählen</option>
          <option value="Männer">Männer</option>
          <option value="Frauen">Frauen</option>
          <option value="Mixed">Gemischt</option>
        </select>

        {members.map((m, i) => (
          <div key={i} className="flex space-x-2 mb-2">
            <input
              type="text"
              placeholder={`Mitglied ${i + 1}${i >= 4 ? " (optional)" : ""}`}
              value={members[i]}
              onChange={(e) => {
                const newMembers = [...members];
                newMembers[i] = e.target.value;
                setMembers(newMembers);
              }}
              className="flex-1 bg-secondary-800 text-white border border-secondary-700 p-2 rounded placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {eventType === "Kinder" && (
              <input
                type="number"
                placeholder="Alter"
                value={ages[i]}
                onChange={(e) => {
                  const newAges = [...ages];
                  newAges[i] = parseInt(e.target.value) || 0;
                  setAges(newAges);
                }}
                className="w-20 bg-secondary-800 text-white border border-secondary-700 p-2 rounded placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            )}
          </div>
        ))}

        <input
          type="email"
          placeholder="Kontakt E-Mail"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          className="w-full bg-secondary-800 text-white border border-secondary-700 p-2 mb-2 rounded placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />

        <input
          type="tel"
          placeholder="Handynummer (optional)"
          value={contactPhone}
          onChange={(e) => setContactPhone(e.target.value)}
          className="w-full bg-secondary-800 text-white border border-secondary-700 p-2 mb-4 rounded placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />

        <div className="flex items-center justify-between gap-2">
          {/* Bewusst abgesetzt von den Aktionen rechts: ein versehentlicher
              Klick neben "Speichern" waere hier besonders teuer. */}
          <button
            onClick={handleDelete}
            disabled={loeschend}
            className="px-3 py-2 rounded text-sm text-red-300 hover:bg-red-500/10 border border-red-500/30 transition-colors disabled:opacity-50"
          >
            {loeschend ? "Löscht…" : "Team löschen"}
          </button>

          <div className="flex space-x-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded bg-secondary-700 hover:bg-secondary-600 text-gray-200 transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 rounded bg-primary-500 text-secondary-950 hover:bg-primary-600 transition-colors"
            >
              Speichern
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
